import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

type TgThemeParams = {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
  section_bg_color?: string;
  destructive_text_color?: string;
};

type TgBackButton = {
  isVisible: boolean;
  show(): void;
  hide(): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
};

type TgMainButton = {
  text: string;
  isVisible: boolean;
  isActive: boolean;
  setText(text: string): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
  show(): void;
  hide(): void;
  enable(): void;
  disable(): void;
  showProgress(leaveActive?: boolean): void;
  hideProgress(): void;
  setParams(params: {
    text?: string;
    color?: string;
    text_color?: string;
    is_active?: boolean;
    is_visible?: boolean;
  }): void;
};

export type TgWebApp = {
  platform?: string;
  isActive?: boolean;
  viewportHeight?: number;
  viewportStableHeight?: number;
  safeAreaInset?: Insets;
  contentSafeAreaInset?: Insets;
  initData: string;
  initDataUnsafe?: { user?: { id: number; first_name?: string; last_name?: string } };
  themeParams: TgThemeParams;
  colorScheme: "light" | "dark";
  ready(): void;
  expand(): void;
  BackButton: TgBackButton;
  MainButton: TgMainButton;
  HapticFeedback?: {
    impactOccurred(style: "light" | "medium" | "heavy" | "rigid" | "soft"): void;
    notificationOccurred(type: "error" | "success" | "warning"): void;
  };
  onEvent(event: string, cb: () => void): void;
  offEvent(event: string, cb: () => void): void;
  openTelegramLink?(url: string): void;
  openLink?(url: string): void;
};

declare global {
  interface Window {
    Telegram?: { WebApp: TgWebApp };
  }
}

export function tg(): TgWebApp | null {
  const app = window.Telegram?.WebApp;
  const platform = app?.platform?.toLowerCase();
  return app && (app.initData || (platform && platform !== "unknown")) ? app : null;
}

export function haptic(type: "success" | "error" | "warning"): void {
  tg()?.HapticFeedback?.notificationOccurred(type);
}

/**
 * Telegram mavzu parametrlaridan ochiq/qorong'i sxemani aniqlaydi va
 * `<html data-scheme>` ga o'rnatadi. CSS qolgan ranglarni o'zi hal qiladi.
 * Telegram bo'lmasa tizim sxemasiga (prefers-color-scheme) tushadi.
 */
export function applyTheme(): void {
  const w = tg();
  const scheme =
    w?.colorScheme ??
    (window.matchMedia?.("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light");
  document.documentElement.dataset.scheme = scheme;
  // Telegram fon rangini blend qilish uchun (mavjud bo'lsa).
  const bg = w?.themeParams?.bg_color;
  if (bg) document.documentElement.style.setProperty("--tg-bg", bg);
}

/** Ochiq oynada Telegram'ga oching xabarini beradi (link/tel). */
export function openExternal(url: string): void {
  const w = tg();
  if (url.startsWith("tel:") || url.startsWith("geo:")) {
    // Qurilma protokollari — dialer yoki o'rnatilgan xarita ilovalari tanlovi.
    window.location.href = url;
    return;
  }
  if (w?.openLink) w.openLink(url);
  else window.open(url, "_blank");
}

/**
 * Telegram BackButton'ni ko'rsatadi va `onClick`ni bog'laydi. `active=false`
 * bo'lsa yashiradi. Komponent unmount bo'lganda tozalaydi.
 */
export function useBackButton(active: boolean, onClick: () => void): void {
  useEffect(() => {
    const w = tg();
    if (!w) return;
    const bb = w.BackButton;
    if (!active) {
      bb.hide();
      return;
    }
    bb.onClick(onClick);
    bb.show();
    return () => {
      bb.offClick(onClick);
      bb.hide();
    };
  }, [active, onClick]);
}

/**
 * Telegram MainButton'ni boshqaradi. `text` berilsa ko'rsatadi va `onClick`ni
 * bog'laydi; `null` bo'lsa yashiradi. `loading` progress ko'rsatadi,
 * `disabled` tugmani o'chiradi. Telegram mavjudligini qaytaradi (in-app
 * tugmani yashirish uchun).
 */
export function useMainButton(opts: {
  text: string | null;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
}): boolean {
  const { text, onClick, loading = false, disabled = false } = opts;
  const present = tg() != null;
  useEffect(() => {
    const w = tg();
    if (!w) return;
    const mb = w.MainButton;
    if (!text) {
      mb.hide();
      return;
    }
    mb.setText(text);
    mb.onClick(onClick);
    if (disabled) mb.disable();
    else mb.enable();
    if (loading) mb.showProgress();
    else mb.hideProgress();
    mb.show();
    return () => {
      mb.offClick(onClick);
      mb.hideProgress();
      mb.hide();
    };
  }, [text, onClick, loading, disabled]);
  return present;
}

type Insets = { top: number; right: number; bottom: number; left: number };
export type TelegramWebApp = TgWebApp;
export const getTelegramWebApp = tg;
export const useTelegramBackButton = useBackButton;
type AppEnvironment = {
  isTelegram: boolean;
  isActive: boolean;
  colorScheme: "light" | "dark";
  viewportHeight: number;
  insets: Insets;
  theme: TgThemeParams;
};
const EnvironmentContext = createContext<AppEnvironment>({
  isTelegram: false, isActive: true, colorScheme: "light",
  viewportHeight: 0, insets: { top: 0, right: 0, bottom: 0, left: 0 }, theme: {},
});
function environment(app: TgWebApp | null, active: boolean): AppEnvironment {
  const insets = { top: 0, right: 0, bottom: 0, left: 0 };
  for (const side of ["top", "right", "bottom", "left"] as const)
    insets[side] = Math.max(0, app?.safeAreaInset?.[side] ?? 0, app?.contentSafeAreaInset?.[side] ?? 0);
  return {
    isTelegram: Boolean(app), isActive: active && document.visibilityState !== "hidden",
    colorScheme: app?.colorScheme ?? (window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light"),
    viewportHeight: app?.viewportStableHeight || app?.viewportHeight || window.innerHeight,
    insets, theme: { ...app?.themeParams },
  };
}
/** Sole SDK initialization and environment-listener owner for both areas. */
export function TelegramProvider({ children }: { children: ReactNode }) {
  const app = tg();
  const [value, setValue] = useState(() => environment(app, app?.isActive ?? true));
  useEffect(() => {
    let active = app?.isActive ?? true;
    const sync = () => { applyTheme(); setValue(environment(app, active)); };
    const activated = () => { active = true; sync(); };
    const deactivated = () => { active = false; sync(); };
    const media = window.matchMedia?.("(prefers-color-scheme: dark)");
    const events: [string, () => void][] = [
      ["themeChanged", sync], ["viewportChanged", sync], ["safeAreaChanged", sync],
      ["contentSafeAreaChanged", sync], ["activated", activated], ["deactivated", deactivated],
    ];
    app?.ready();
    app?.expand();
    for (const [event, listener] of events) app?.onEvent(event, listener);
    document.addEventListener("visibilitychange", sync);
    window.addEventListener("resize", sync);
    if (!app) media?.addEventListener?.("change", sync);
    sync();
    return () => {
      for (const [event, listener] of events) app?.offEvent(event, listener);
      document.removeEventListener("visibilitychange", sync);
      window.removeEventListener("resize", sync);
      if (!app) media?.removeEventListener?.("change", sync);
    };
  }, [app]);
  return <EnvironmentContext.Provider value={value}>{children}</EnvironmentContext.Provider>;
}
export function useAppEnvironment() { return useContext(EnvironmentContext); }
